import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { Autocomplete, Box, Stack, TextField } from '@mui/material';

import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { UpdateIcon, CancelButton, UpdateButton } from '../../components/mui/Buttons/Buttons';
import {
  updateSkillxAssignment,
  getSkillxAssignments,
} from '../../redux/features/skillxSlice';
import { getAvailableCourses } from '../../redux/features/coursesSlice';

const ValidationSchema = Yup.object().shape({
  title: Yup.string().trim().required('Title is required').max(200, 'Title is too long'),
  description: Yup.string().trim().required('Description is required'),
  course_available_id: Yup.number()
    .typeError('Course ID must be a number')
    .integer('Course ID must be a whole number')
    .positive('Course ID must be positive')
    .nullable(),
  max_grade: Yup.number()
    .typeError('Max grade must be a number')
    .min(1, 'Max grade must be at least 1')
    .nullable(),
});

export default function UpdateSkillxAssignment({ rowData, dispatchparams }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);
  const { availableCourse } = useSelector((state) => state.courses);
  const courseOptions = Array.isArray(availableCourse) ? availableCourse : [];

  // Fetch the SkillX course list once the dialog is opened.
  useEffect(() => {
    if (open && !courseOptions.length) {
      dispatch(getAvailableCourses({ skillx: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: rowData?.title || '',
      module_label: rowData?.module_label || '',
      course_available_id: rowData?.course_available_id ?? '',
      description: rowData?.description || '',
      instructions: rowData?.instructions || '',
      due_date: rowData?.due_date ? moment(rowData.due_date).format('YYYY-MM-DD') : '',
      max_grade: rowData?.max_grade ?? 100,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const payload = {
        id: rowData?.id,
        title: values.title,
        description: values.description,
        module_label: values.module_label || null,
        instructions: values.instructions || null,
        course_available_id: values.course_available_id || null,
        due_date: values.due_date || null,
        max_grade: values.max_grade || 100,
      };

      dispatch(updateSkillxAssignment(payload)).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          dispatch(getSkillxAssignments(dispatchparams));
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload?.message || 'Failed to update assignment', {
            variant: 'error',
          });
        }
      });
    },
  });

  const { errors, touched, values, handleChange, handleSubmit } = formik;

  return (
    <GlobalDialog
      title="Update Assignment"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="md"
      closeButton
      popupButton={
        <UpdateIcon size="small" action={() => setOpen(true)}>
          Edit
        </UpdateIcon>
      }
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit}>
            <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
              <TextField
                name="title"
                label="Title"
                fullWidth
                value={values.title}
                onChange={handleChange}
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  name="module_label"
                  label="Module (optional)"
                  fullWidth
                  value={values.module_label}
                  onChange={handleChange}
                />
                <Autocomplete
                  fullWidth
                  options={courseOptions}
                  getOptionLabel={(option) => option?.courseName || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    courseOptions.find((c) => c.id === values.course_available_id) || null
                  }
                  onChange={(e, newValue) =>
                    formik.setFieldValue('course_available_id', newValue ? newValue.id : '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="course_available_id"
                      label="Course (optional)"
                      error={touched.course_available_id && Boolean(errors.course_available_id)}
                      helperText={touched.course_available_id && errors.course_available_id}
                    />
                  )}
                />
              </Stack>

              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                minRows={3}
                value={values.description}
                onChange={handleChange}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
              />

              <TextField
                name="instructions"
                label="Instructions (optional)"
                fullWidth
                multiline
                minRows={2}
                value={values.instructions}
                onChange={handleChange}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  name="due_date"
                  label="Due Date (optional)"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={values.due_date}
                  onChange={handleChange}
                />
                <TextField
                  name="max_grade"
                  label="Max Grade"
                  type="number"
                  fullWidth
                  value={values.max_grade}
                  onChange={handleChange}
                  error={touched.max_grade && Boolean(errors.max_grade)}
                  helperText={touched.max_grade && errors.max_grade}
                />
              </Stack>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
              <Stack direction="row" spacing={1}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Cancel
                </CancelButton>
                <UpdateButton disabled={loading} size="small" type="submit">
                  {loading ? 'Please wait...' : 'Update'}
                </UpdateButton>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}
